import type { ErrorCardProps, LoadingErrorWrapperProps } from '@/types';
import Loader from '../loader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertTriangle,
  MoveLeft,
  RefreshCw,
  Wifi,
  WifiOff,
} from 'lucide-react';
import type { ApiErrorResponse } from '@/types/async-boundary.types';
import { Button } from '@/components/ui/button';
import { getMessageByStatus } from '@/lib';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

const ErrorCard = <T,>({ error, onRetry, isFetching }: ErrorCardProps<T>) => {
  const navigate = useNavigate();
  const getErrorInfo = (error: ApiErrorResponse) => {
    if (!error) {
      return {
        title: 'Unknown Error',
        message: 'An unexpected error occurred. Please try again.',
        status: 500,
        code: 'ERR_UNKNOWN',
      };
    }

    const status = error.status || 500;

    const getErrorTitle = (status: number) => {
      if (status >= 500) return 'Server Error';
      if (status >= 400) return 'Request Error';
      return 'Connection Error';
    };

    return {
      title: getErrorTitle(status),
      message: getMessageByStatus(status),
      status: status,
      code: `HTTP_${status}`,
    };
  };

  const errorInfo = getErrorInfo(error);

  return (
    <Alert className="border-red-300 bg-red-50 dark:bg-red-800/10 dark:border-red-800/30">
      <AlertTitle className="flex items-center gap-2">
        <div className="flex-shrink-0">
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/30`}
          >
            {errorInfo.status >= 500 ? (
              <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
            ) : errorInfo.status >= 400 ? (
              <WifiOff className="h-4 w-4 text-orange-600 dark:text-orange-400" />
            ) : (
              <Wifi className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            )}
          </div>
        </div>

        <h3 className={`text-sm font-semibold text-red-800 dark:text-red-300`}>
          {errorInfo.title}
        </h3>
      </AlertTitle>
      <AlertDescription
        className={`text-sm leading-relaxed text-red-700 dark:text-red-300 mt-4`}
      >
        {errorInfo.message}
        <div
          className={`mt-3 p-2 rounded border bg-red-100/50 dark:bg-red-900/20 border-red-200/50 dark:border-red-800/30`}
        >
          <div className="flex items-center justify-between">
            <span
              className={`text-xs font-mono text-red-600 dark:text-red-400`}
            >
              {errorInfo.code}
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-2 mt-4">
          {onRetry && (
            <Button
              onClick={() => onRetry()}
              size={'sm'}
              variant={'destructive'}
            >
              <RefreshCw
                className={cn('h-3 w-3', isFetching && 'animate-spin')}
              />
              {isFetching ? 'Fetching...' : 'Try Again'}
            </Button>
          )}

          {onRetry && (
            <Button
              onClick={() => {
                navigate('/'); // Back to home
              }}
              size={'sm'}
              variant={'destructive'}
            >
              <MoveLeft
                className={cn('h-3 w-3')}
              />
              Back to home
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};

const WithLoadingErrorWrapper = <T,>({
  loading,
  error,
  children,
  isFetching,
  onRetry,
  className,
}: LoadingErrorWrapperProps<T>) => {
  if (loading)
    return (
      <div className="p-2 w-full h-full flex items-center justify-center">
        {' '}
        <Loader />
      </div>
    );
  if (error)
    return (
      <ErrorCard error={error} onRetry={onRetry} isFetching={isFetching} />
    );

  return (
    <div className={cn('relative', className)}>
      {children}
      {isFetching && (
        <div className="absolute inset-0 bg-gray-200 z-50 text-gray-900 font-semibold flex items-center justify-center opacity-60 pointer-events-none z-10">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default WithLoadingErrorWrapper;
